import { RfqTemplateType } from '@lune-fe/dapi-models'
import { Text } from '@lune-fe/lune-ui-lib'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'

import useTitle from 'hooks/useTitle'
import ClientAccountMappingFields, {
    ClientAccountMappingResult,
    SheetData,
} from 'views/CalculateEmissions/RFQ/components/ClientAccountMappingFields'
import { StageContext } from 'views/CalculateEmissions/RFQ/components/MultiStepFlow'
import ProgressSteps, { MappingSteps } from 'views/CalculateEmissions/RFQ/components/ProgressSteps'
import SharedLayout from 'views/CalculateEmissions/RFQ/components/SharedLayout'
import { getMappingInfo } from 'views/CalculateEmissions/RFQ/utils/operationUtils'
import { RFQState, RFQTemplateState } from 'views/CalculateEmissions/RFQ/utils/types'

const ClientAccountDetails = ({
    sheetType,
    context,
    hideProgressSteps,
    nextButtonLabel: nextButtonLabelOverride,
    onBack,
}: {
    sheetType: RfqTemplateType
    context: StageContext
    hideProgressSteps?: boolean
    nextButtonLabel?: string
    onBack?: () => void
}) => {
    const { title } = getMappingInfo(sheetType)
    useTitle(`Client Accounts - ${title}`)
    const { enqueueSnackbar: snackbar } = useSnackbar()

    const [state, setState] = useState<RFQTemplateState | undefined>()
    const formRef = useRef<HTMLFormElement>(null)

    useEffect(() => {
        if (!context.state) {
            snackbar(`File could not be found.`)
            return
        }
        const storedState = context.state
        if (!storedState.sheet) {
            throw new Error(`Programming error. State should be filled and with data`)
        }
        setState(storedState as RFQTemplateState)
    }, [])

    const sheetData: SheetData | undefined = state
        ? {
              sheet: state.sheet,
              headers: state.headers,
              exampleRow: state.exampleRow,
              headerStartIndex: state.headerStartIndex,
              headerRow: state.headerRow,
              endRow: state.endRow,
          }
        : undefined

    const handleSubmit = (result: ClientAccountMappingResult) => {
        if (!state) return

        const newState: RFQState = {
            ...state,
            clientAccountName: result.clientAccountName,
            clientAccountCurrency: result.clientAccountCurrency,
            autoFillState: {
                ...state.autoFillState,
                clientAccountName: {
                    column: false,
                    mapping: false,
                },
                clientAccountCurrency: {
                    column: false,
                    mapping: false,
                },
            },
        } as RFQState
        context.updateState(newState)
        context.navigate('shippingDetails')
    }

    return (
        <SharedLayout
            loading={!state}
            handleBack={onBack}
            stepsComponent={
                <>
                    {!hideProgressSteps && (
                        <ProgressSteps step={MappingSteps.CLIENT_ACCOUNTS} showClientAccounts />
                    )}
                    <Text variant={'h4'}>Client accounts</Text>
                </>
            }
            handleNext={() => {
                if (formRef.current) {
                    formRef.current.requestSubmit()
                }
            }}
            nextButtonLabel={nextButtonLabelOverride ?? 'Shipping details'}
        >
            {sheetData && (
                <ClientAccountMappingFields
                    sheetData={sheetData}
                    initialClientAccountName={state?.clientAccountName}
                    initialClientAccountCurrency={state?.clientAccountCurrency}
                    onSubmit={handleSubmit}
                    formRef={formRef}
                />
            )}
        </SharedLayout>
    )
}

export default ClientAccountDetails
